/**
 * Generic CSV export utility for browser-side data download.
 *
 * - Generates CSV with UTF-8 BOM header for Chinese Excel compatibility.
 * - Triggers browser download via a temporary <a> element.
 * - Filename convention: `{prefix}_{dataType}_{YYYYMMDD}.csv`
 */

/** UTF-8 BOM bytes for Excel Chinese character recognition. */
const BOM = "﻿";

/**
 * Escape a single CSV field.
 * Wraps the value in double-quotes if it contains commas, quotes, or newlines.
 * Doubles any internal double-quote characters per RFC 4180.
 */
function escapeCsvField(value: string): string {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Format a Date or ISO string as YYYY-MM-DD.
 */
function formatDateStamp(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Build a timestamped filename.
 *
 * @param prefix - Typically the matter name or short identifier.
 * @param dataType - e.g. "timentries", "matters", "tasks".
 * @returns `{prefix}_{dataType}_{YYYYMMDD}.csv`
 */
function buildFilename(prefix: string, dataType: string): string {
  const safePrefix = prefix.replace(/[\\/:*?"<>|]/g, "_").trim() || "export";
  const stamp = formatDateStamp(new Date()).replace(/-/g, "");
  return `${safePrefix}_${dataType}_${stamp}.csv`;
}

/**
 * Export tabular data as a CSV file download.
 *
 * @param filename - Full file name including `.csv` extension.
 * @param headers  - Column header strings (will be escaped).
 * @param rows     - Array of row arrays; each inner array matches header order.
 */
export function exportToCsv(
  filename: string,
  headers: string[],
  rows: string[][],
): void {
  const headerLine = headers.map(escapeCsvField).join(",");
  const dataLines = rows.map((row) =>
    row.map((cell) => escapeCsvField(cell ?? "")).join(","),
  );
  const csvContent = BOM + headerLine + "\n" + dataLines.join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();

  // Clean up
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Convenience: build filename then export.
 */
export function exportData(
  prefix: string,
  dataType: string,
  headers: string[],
  rows: string[][],
): void {
  const filename = buildFilename(prefix, dataType);
  exportToCsv(filename, headers, rows);
}
